import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  Subscription,
  SubscriptionPlan,
  BillingHistoryItem,
  FeatureAccess,
  CreateSubscriptionRequest,
  UpdateSubscriptionRequest,
  CancelSubscriptionRequest,
  FeatureType,
  SubscriptionTier
} from '../types';
import { subscriptionApi, setSubscriptionApiUnauthorizedCallback } from '../services/subscriptionApi';
import toast from 'react-hot-toast';

interface SubscriptionState {
  // State
  subscription: Subscription | null;
  plans: SubscriptionPlan[];
  billingHistory: BillingHistoryItem[];
  featureAccess: FeatureAccess | null;
  isLoading: boolean;
  isCreatingSubscription: boolean;
  isUpdatingSubscription: boolean;
  isCancelingSubscription: boolean;
  isLoadingPlans: boolean;
  isLoadingBillingHistory: boolean;
  isLoadingFeatureAccess: boolean;
  error: string | null;

  // Actions
  loadSubscription: () => Promise<void>;
  loadPlans: () => Promise<void>;
  loadBillingHistory: () => Promise<void>;
  loadFeatureAccess: () => Promise<void>;
  createCheckoutSession: (request: CreateSubscriptionRequest) => Promise<{ checkoutUrl: string; sessionId: string }>;
  updateSubscription: (request: UpdateSubscriptionRequest) => Promise<boolean>;
  cancelSubscription: (request: CancelSubscriptionRequest) => Promise<boolean>;
  openCustomerPortal: () => Promise<void>;
  canAccessFeature: (feature: FeatureType) => boolean;
  getCurrentTier: () => SubscriptionTier;
  hasActiveSubscription: () => boolean;
  isInTrial: () => boolean;
  isInGracePeriod: () => boolean;
  isTrialExpired: () => boolean;
  getTrialDaysRemaining: () => number | null;
  getGracePeriodDaysRemaining: () => number | null;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  subscription: null,
  plans: [],
  billingHistory: [],
  featureAccess: null,
  isLoading: false,
  isCreatingSubscription: false,
  isUpdatingSubscription: false,
  isCancelingSubscription: false,
  isLoadingPlans: false,
  isLoadingBillingHistory: false,
  isLoadingFeatureAccess: false,
  error: null,
};

export const useSubscriptionStore = create<SubscriptionState>()(
  devtools(
    (set, get) => {
      // Set up unauthorized callback
      setSubscriptionApiUnauthorizedCallback(() => {
        // This will be called by authStore when user is logged out
        set(initialState);
      });

      return {
        ...initialState,

        loadSubscription: async () => {
          set({ isLoading: true, error: null });
          
          try {
            const subscription = await subscriptionApi.getCurrentSubscription();
            set({
              subscription,
              isLoading: false,
              error: null,
            });
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load subscription';
            
            // Handle 404 (no subscription found) as a valid state, not an error
            if (errorMessage.includes('404') || errorMessage.includes('No subscription found')) {
              set({
                subscription: null,
                isLoading: false,
                error: null, // Don't set error for 404
              });
              return;
            }
            
            set({
              error: errorMessage,
              isLoading: false,
            });
            
            // Don't show toast for 401 errors as they're handled by auth
            if (!errorMessage.includes('Unauthorized')) {
              toast.error(errorMessage);
            }
          }
        },

        loadPlans: async () => {
          set({ isLoadingPlans: true, error: null });
          
          try {
            const plans = await subscriptionApi.getSubscriptionPlans();
            set({
              plans,
              isLoadingPlans: false,
              error: null,
            });
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load subscription plans';
            set({
              error: errorMessage,
              isLoadingPlans: false,
            });
            toast.error(errorMessage);
          }
        },

        loadBillingHistory: async () => {
          set({ isLoadingBillingHistory: true, error: null });
          
          try {
            const billingHistory = await subscriptionApi.getBillingHistory();
            set({
              billingHistory,
              isLoadingBillingHistory: false,
              error: null,
            });
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load billing history';
            set({
              error: errorMessage,
              isLoadingBillingHistory: false,
            });
            
            if (!errorMessage.includes('Unauthorized')) {
              toast.error(errorMessage);
            }
          }
        },

        loadFeatureAccess: async () => {
          set({ isLoadingFeatureAccess: true, error: null });
          
          try {
            const featureAccess = await subscriptionApi.getFeatureAccess();
            set({
              featureAccess,
              isLoadingFeatureAccess: false,
              error: null,
            });
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load feature access';
            set({
              error: errorMessage,
              isLoadingFeatureAccess: false,
            });
            
            if (!errorMessage.includes('Unauthorized')) {
              toast.error(errorMessage);
            }
          }
        },

        createCheckoutSession: async (request: CreateSubscriptionRequest) => {
          set({ isCreatingSubscription: true, error: null });
          
          try {
            const result = await subscriptionApi.createCheckoutSession(request);
            set({
              isCreatingSubscription: false,
              error: null,
            });
            
            return result;
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to create checkout session';
            set({
              error: errorMessage,
              isCreatingSubscription: false,
            });
            toast.error(errorMessage);
            throw error;
          }
        },



        updateSubscription: async (request: UpdateSubscriptionRequest) => {
          set({ isUpdatingSubscription: true, error: null });
          
          try {
            const subscription = await subscriptionApi.updateSubscription(request);
            
            // Reload feature access after updating subscription
            const featureAccess = await subscriptionApi.getFeatureAccess();
            
            set({
              subscription,
              featureAccess,
              isUpdatingSubscription: false,
              error: null,
            });
            
            toast.success('Subscription updated successfully!');
            return true;
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to update subscription';
            set({
              error: errorMessage,
              isUpdatingSubscription: false,
            });
            toast.error(errorMessage);
            return false;
          }
        },

        cancelSubscription: async (request: CancelSubscriptionRequest) => {
          set({ isCancelingSubscription: true, error: null });
          
          try {
            const subscription = await subscriptionApi.cancelSubscription(request);
            
            // Reload feature access after canceling subscription
            const featureAccess = await subscriptionApi.getFeatureAccess();
            
            set({
              subscription,
              featureAccess,
              isCancelingSubscription: false,
              error: null,
            });
            
            const message = request.cancelAtPeriodEnd 
              ? 'Subscription will be canceled at the end of the billing period'
              : 'Subscription canceled successfully';
            toast.success(message);
            return true;
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to cancel subscription';
            set({
              error: errorMessage,
              isCancelingSubscription: false,
            });
            toast.error(errorMessage);
            return false;
          }
        },

        openCustomerPortal: async () => {
          set({ error: null });
          
          try {
            const { url } = await subscriptionApi.createCustomerPortalSession();
            window.open(url, '_blank');
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to open customer portal';
            set({ error: errorMessage });
            toast.error(errorMessage);
          }
        },

        canAccessFeature: (feature: FeatureType) => {
          const { featureAccess } = get();
          
          if (!featureAccess) {
            return feature === FeatureType.BasicHabits; // Only basic habits available without feature access data
          }
          
          switch (feature) {
            case FeatureType.BasicHabits:
              return true; // Always available
            case FeatureType.Analytics:
              return featureAccess.canAccessAnalytics;
            case FeatureType.Export:
              return featureAccess.canExportData;
            case FeatureType.PrioritySupport:
              return featureAccess.hasPrioritySupport;
            case FeatureType.HabitLimit:
              return featureAccess.canCreateMoreHabits;
            case FeatureType.WeeklyInsights:
              return featureAccess.canAccessWeeklyInsights;
            default:
              return false;
          }
        },

        getCurrentTier: () => {
          const { featureAccess } = get();
          return featureAccess?.currentTier ?? SubscriptionTier.Free;
        },

        hasActiveSubscription: () => {
          const { featureAccess } = get();
          return featureAccess?.hasActiveSubscription ?? false;
        },

        isInTrial: () => {
          const { featureAccess } = get();
          return featureAccess?.isInTrial ?? false;
        },

        isInGracePeriod: () => {
          const { featureAccess } = get();
          return featureAccess?.isInGracePeriod ?? false;
        },

        isTrialExpired: () => {
          const { featureAccess } = get();
          return featureAccess?.isTrialExpired ?? false;
        },

        getTrialDaysRemaining: () => {
          const { featureAccess } = get();
          return featureAccess?.trialDaysRemaining ?? null;
        },

        getGracePeriodDaysRemaining: () => {
          const { featureAccess } = get();
          return featureAccess?.gracePeriodDaysRemaining ?? null;
        },

        clearError: () => {
          set({ error: null });
        },

        reset: () => {
          set(initialState);
        },
      };
    },
    {
      name: 'subscription-store',
    }
  )
); 